import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SaiProductFrame, { nextProductAfter } from "./sai/SaiProductFrame";
import StepTabs from "./sai/StepTabs";
import { PRODUCT_ICONS } from "./sai/productIcons";
import { productByKey, stageByKey } from "./sai/products";
import { IDS } from "./sai/ids";
import { screenIdFor } from "./sai/trapezium";
import IntelligenceVisual from "./IntelligenceVisual";
import "./IntelligenceLayer.css";

// The intelligence layer, ported from sai's shared ProductScreen (brochure-demo
// @ 2a7c405, the template his /demo/intelligence renders) into our shell:
// SaiProductFrame renders our <Screen>; IDs are data-trapezium-* via
// toTrapezium; "Next product" follows our STEPS (none after this page, as in
// his: the next step is Packages, not a product).
//
// His notes: a headline and one line, then three steps. Headline and lede are
// ours (Harsh, 2026-10-17): one layer under every product, built with doctors. Each step shows a
// drawn screen beside its words and three short points. Content: his
// data/products.js (PAGES.intelligence).

const SCREEN = "intelligence"; // his screen key (our product.intelligence-layer)

const PAGE = {
  head: <>One intelligence layer, <em>under every product.</em></>,
  lede: "Built and reviewed with doctors over years — the clinical knowledge behind every report, app and message.",
  steps: [
    { key: "knowledge", nav: "Knowledge", head: "1,500+ biomarkers, each explained.",
      lede: "Every sentence comes from an approved explanation.",
      visual: { type: "explain", name: "LDL cholesterol", value: "146", unit: "mg/dL",
        states: [["Low", "Below range: rarely a concern on its own."], ["Normal", "In range: keep doing what you are doing."], ["High", "Above range: raises heart risk over time. Recheck in 12 weeks."]], on: 2 },
      points: [["Reviewed by doctors", "One approved text per marker"], ["High, low, borderline", "Wording for each"], ["Clinical rules", "Scores and personas, configured"]] },
    { key: "data-in", nav: "Data in", head: "Your test codes, matched to ours.",
      lede: "Your codes, matched to ours. LOINC too.",
      visual: { type: "mapping", rows: [["HBA1C-GLY", "HbA1c"], ["LDL_DIRECT", "LDL Cholesterol"], ["VIT D 25-OH", "Vitamin D (25-OH)"]], formats: ["HL7", "CSV", "XLS", "XML", "PDF"] },
      points: [["Auto-matched", "Your codes to ours"], ["Any format in", "HL7, CSV, XLS, XML, PDF"], ["Unmapped queue", "Nothing slips through"]] },
    { key: "portal", nav: "Portal", head: "Configure it, track it, audit it.",
      lede: "Branding, content and every report, in one portal.",
      visual: { type: "stack", lit: ["portal", "security"] },
      points: [["Client portal", "Branding, content, report logs"], ["Encrypted", "Hosted in India (Mumbai)"], ["Auditable", "Every change and report logged"]] },
  ],
};

export default function IntelligenceLayer() {
  const screenId = screenIdFor(SCREEN);
  const product = productByKey(SCREEN);
  const stage = stageByKey(product.stage);
  const Icon = PRODUCT_ICONS[product.icon];
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  const step = PAGE.steps[active];
  const last = active === PAGE.steps.length - 1;
  const nextProduct = nextProductAfter(screenId);
  const idBase = `product-${SCREEN}`;

  const onMore = () => {
    if (!last) setActive(active + 1);
    else if (nextProduct) navigate(nextProduct.route);
  };

  return (
    <SaiProductFrame screenId={screenId} state={`step-${step.key}`}>
      <div className="prod-page" data-stage={product.stage}>
        <p className="prod-kicker">
          <i aria-hidden="true"><Icon size={15} /></i>
          {product.name}
          <span>· {stage ? stage.label : "Foundation"}</span>
        </p>
        <h1>{PAGE.head}</h1>
        {PAGE.lede ? <p className="prod-lede">{PAGE.lede}</p> : null}

        <StepTabs
          steps={PAGE.steps.map((s) => s.nav)}
          active={active}
          onChange={setActive}
          idBase={idBase}
          trapeziumId={(i) => IDS.step(SCREEN, PAGE.steps[i].key)}
          label={`${product.name}: steps`}
        />

        <div
          className="prod-stage"
          id={`${idBase}-panel`}
          role="tabpanel"
          aria-labelledby={`${idBase}-tab-${active}`}
        >
          <div className="prod-visual">
            <IntelligenceVisual key={active} visual={step.visual} />
          </div>
          <div className="prod-copy">
            {/* Keyed: the words re-enter on each step; the Next button below stays put. */}
            <Fragment key={active}>
              <h2>{step.head}</h2>
              <p>{step.lede}</p>
              <ul className="prod-points">
                {step.points.map(([t, s], i) => (
                  <li key={t} style={{ "--i": i }}>
                    <i aria-hidden="true"><Check size={14} strokeWidth={3} /></i>
                    <span><b>{t}</b><span>{s}</span></span>
                  </li>
                ))}
              </ul>
            </Fragment>
            {last && !nextProduct ? null : (
              <button type="button" className="prod-next" data-trapezium-id={IDS.more(SCREEN)} onClick={onMore}>
                {last ? `Next product: ${nextProduct.title}` : `Next: ${PAGE.steps[active + 1].nav}`}
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </SaiProductFrame>
  );
}
